import React, { useState, useMemo } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Form,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane
} from 'reactstrap';
import TableContainerReactTable from '../../../../../Components/Common/TableContainerReactTable';
import SelectInput from '../../common/SelectInput';
import FormInput from '../../common/FormInput';
import classnames from 'classnames';
import { useLazyGetVendorsByVendorNameQuery } from 'features/organizations/redux/organizationsApiSlice';
import LoadingIndicator from 'Components/Loading/LoadingIndicator';

export interface ManufacturerData {
  rowId: number;
  name: string;
  location: string;
  type: string;
  industry: string;
  active: boolean;
}

export interface ManufacturerSearchForm {
  searchField: string;
  searchValue: string;
}

interface AssociateManufacturerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect?: (manufacturer: ManufacturerData) => void;
}

const defaultSearchForm: ManufacturerSearchForm = {
  searchField: 'Name',
  searchValue: ''
};



const searchFieldOptions = ['Name', 'Location', 'Type', 'Industry'];

const AssociateManufacturerModal: React.FC<AssociateManufacturerModalProps> = ({
  isOpen,
  onClose,
  onSelect
}) => {
  const [searchForm, setSearchForm] = useState<ManufacturerSearchForm>(defaultSearchForm);
  const [selectedManufacturer, setSelectedManufacturer] = useState<ManufacturerData | null>(null);
  const [activeTab] = useState('1'); // Only one tab, but keep for extensibility

  const handleSearchFieldChange = (field: keyof ManufacturerSearchForm, value: string) => {
    setSearchForm(prev => ({ ...prev, [field]: value }));
  };

  //Redux query
  const [getManufacturersByManufacturerName, { data: manufacturersData, isLoading: isManufacturersLoading }] = useLazyGetVendorsByVendorNameQuery();

  const handleSearch = async () => {
    try {
      // Use utility function to format search parameters with '%' default
      const searchParam = searchForm.searchValue.trim() || '%';
      await getManufacturersByManufacturerName({ vendorName: searchParam });
    } catch (error) {
      console.error('Search failed:', error);
    }
  };

  const handleClear = () => {
    setSearchForm(defaultSearchForm);
    setSelectedManufacturer(null);
  };

  const handleRowClick = (manufacturer: ManufacturerData) => {
    setSelectedManufacturer(manufacturer);
    // Immediately select the manufacturer when row is clicked and close modal
    if (onSelect) {
      onSelect(manufacturer);
    }
    onClose(); // Close modal after selection
  };

  const handleRowDoubleClick = (manufacturer: ManufacturerData) => {
    if (onSelect) {
      onSelect(manufacturer);
    }
    onClose();
  };

  const handleCancel = () => {
    setSelectedManufacturer(null);
    onClose();
  };


  const handleClears = () => {
    setSelectedManufacturer(null);
  };

  // Define columns for the table
  const columns = useMemo(() => [
    {
      header: 'Name',
      accessorKey: 'name',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
          style={{ cursor: 'pointer' }}
          className={selectedManufacturer?.rowId === cell.row.original.rowId ? 'table-active' : ''}
        >
          <span className="text-link" style={{ textDecoration: 'underline' }}>
            {cell.getValue()}
          </span>
        </div>
      )
    },
    {
      header: 'Location',
      accessorKey: 'location',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
          style={{ cursor: 'pointer' }}
          className={selectedManufacturer?.rowId === cell.row.original.rowId ? 'table-active' : ''}
        >
          {cell.getValue()}
        </div>
      )
    },
    {
      header: 'Type',
      accessorKey: 'type',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
          style={{ cursor: 'pointer' }}
          className={selectedManufacturer?.rowId === cell.row.original.rowId ? 'table-active' : ''}
        >
          {cell.getValue()}
        </div>
      )
    },
    {
      header: 'Industry',
      accessorKey: 'industry',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
          style={{ cursor: 'pointer' }}
          className={selectedManufacturer?.rowId === cell.row.original.rowId ? 'table-active' : ''}
        >
          {cell.getValue()}
        </div>
      )
    },
    {
      header: "Active",
      accessorKey: "activeFlag",
      enableColumnFilter: false,
      cell: (cell: any) => {
        const isActive = cell.getValue();
        return isActive ? (
          <div className="text-start ps-1">
            <i className="ri-checkbox-circle-line fs-17 align-middle"></i>
          </div>
        ) : null;
      },
    }
  ], [selectedManufacturer]);

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>Associate Manufacturer</ModalHeader>
      <ModalBody>
        {/* Tab Navigation */}
        <Nav pills className="nav nav-tabs-custom arrow-navtabs nav-success mb-3">
          <NavItem>
            <NavLink
              style={{ cursor: 'pointer' }}
              className={classnames({ active: activeTab === '1' })}
              tabIndex={0}
              aria-label="Search Tab"
              onClick={() => { }}
              onKeyDown={() => { }}
            >
              Search
            </NavLink>
          </NavItem>
        </Nav>
        <TabContent activeTab={activeTab} className="text-muted">
          <TabPane tabId="1">
            <Form>
              <Row className="mb-2 justify-content-center align-items-center gap-2">
                <Col md={4}>
                  <SelectInput
                    formData={searchForm}
                    setFormData={setSearchForm}
                    label="Search"
                    id="searchField"
                    options={searchFieldOptions}
                  />
                </Col>
                <Col md={4}>
                  <FormInput
                    id="searchValue"
                    label="For value"
                    value={searchForm.searchValue}
                    onChange={(val) => handleSearchFieldChange('searchValue', val)}
                    type="text"
                  />
                </Col>
                <Col md={1} className="me-3">
                  <button
                    type="button"
                    onClick={() => handleSearch()}
                    style={{ minWidth: 80 }}
                    className="btn btn-soft-primary btn-sm"
                  >
                    Search
                  </button>
                </Col>
                <Col md={1}>
                  <button
                    type="button"
                    onClick={handleClear}
                    style={{ minWidth: 80 }}
                    className="btn btn-soft-warning btn-sm"
                  >
                    Clear
                  </button>
                </Col>
              </Row>

              {/* Manufacturer Table */}
              <Row>
                <Col xs={12}>
                  {isManufacturersLoading ? (
                    <LoadingIndicator
                      isLoading={isManufacturersLoading}
                      isProcessing={false}
                      error={null}
                      title="Loading manufacturers from server..."
                      height="200px"
                    />
                  ) : (
                    <TableContainerReactTable
                      columns={columns}
                      data={manufacturersData?.data || []}
                      isGlobalFilter={false}
                      customPageSize={10}
                      divClass="mb-0"
                      tableClass="table-centered align-middle table-nowrap mb-0"
                      theadClass="table-light"
                      thClass="px-3 py-2"
                      verticalLines={true}
                    />
                  )}
                </Col>
              </Row>
            </Form>
          </TabPane>
        </TabContent>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-end">
        <button
          onClick={handleClears}
          className="btn btn-soft-warning btn-sm"
          style={{ minWidth: 80 }}
        >
          Clears
        </button>
        <button
          className="btn btn-soft-dark btn-sm"
          onClick={handleCancel}
          style={{ minWidth: 80 }}
        >
          Cancel
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default AssociateManufacturerModal;
